import React, { useState, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { editNewsDetails, updateNewsDetails } from "../../services/newsService/news";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";

const NewsEditModal = ({ isOpen, onClose, newsId, onUpdate }) => {
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const modules_quill = {
    toolbar: [
      [{ header: "1" }, { header: "2" }, { font: [] }],
      [{ size: [] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
      [{ color: [] }], // Text color
      ["clean"], // Clear formatting
    ],
  };

  const formats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "color",
  ];

  useEffect(() => {
    if (isOpen && newsId) {
      fetchNewsDetails();
    }
  }, [isOpen, newsId]);

  const fetchNewsDetails = async () => {
    setIsLoading(true);
    try {
      const response = await editNewsDetails({ news_id: newsId });
      if (response?.data) {
        setFormData(response.data);
      } else {
        throw new Error("Failed to fetch news details.");
      }
    } catch (error) {
      console.error("Error fetching News details:", error);
      showErrorToast(error.response?.data?.message || "Error fetching News details.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuillChange = (content) => {
    setFormData((prev) => ({
      ...prev,
      news: content,
    }));
  };

  const handleSubmit = async () => {
    if (!formData?.news) {
      showErrorToast("News content cannot be empty.");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = { news_id: newsId, ...formData };
      const response = await updateNewsDetails({ payload });

      if (response?.status?.code === 200) {
        showSuccessToast(response.status.message || "News details updated successfully!");
        onClose(); // Close the modal
        if (onUpdate) onUpdate(); // Trigger parent component refresh
      } else {
        throw new Error(response?.status?.message || "Failed to update news.");
      }
    } catch (error) {
      console.error("Error updating News:", error);
      showErrorToast(error.response?.data?.message || "Error updating News details.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-[100%] max-w-3xl overflow-auto">
        <div className="p-2 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold">Edit News</h2>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-900 text-2xl font-bold"
            aria-label="Close"
          >
            &times;
          </button>
        </div>
        <div className="p-2">
          {isLoading ? (
            <p className="text-center text-gray-500">Loading...</p>
          ) : (
            formData && (
              <div>
                <div className="mb-5">
                  <ReactQuill
                    theme="snow"
                    value={formData?.news || ""}
                    onChange={handleQuillChange}
                    modules={modules_quill}
                    formats={formats}
                    placeholder="Enter News content..."
                    className="w-full h-24"
                  />
                </div>


<div className="mt-16">
  <label className="font-semibold text-gray-700">Select Scope</label>
  <select
    className="w-full border p-2 rounded-md mt-1"
    value={formData?.scope || ""}
    onChange={(e) =>
      setFormData((prev) => ({ ...prev, scope: e.target.value }))
    }
  >
    <option value="">Select Scope</option>
    <option value="login">Login</option>
    <option value="dashboard">Dashboard</option>
    <option value="both">Both</option>
  </select>
</div>
<div className="mt-16">
    <label className="font-semibold text-gray-700">Enter Color Code</label>
  <input
    type="text"
       className="w-full border p-2 rounded-md mt-1"
    value={formData.colorCode}
    placeholder='Enter Color Code'
    onChange={(e) =>
      setFormData((prev) => ({ ...prev, colorCode: e.target.value }))
    }
  />
<p className="mt-2">
  Like.
  <span
    className="text-yellow-500 cursor-pointer font-semibold ml-1"
    onClick={() =>
      setFormData((prev) => ({ ...prev, colorCode: "#FFC107" }))
    }
  >
    Yellow → #FFC107
  </span>
  ,{" "}
  <span
    className="text-red-500 cursor-pointer font-semibold"
    onClick={() =>
      setFormData((prev) => ({ ...prev, colorCode: "#F44336" }))
    }
  >
    Red → #F44336
  </span>
  ,{" "}
  <span
    className="text-green-500 cursor-pointer font-semibold"
    onClick={() =>
      setFormData((prev) => ({ ...prev, colorCode: "#4CAF50" }))
    }
  >
    Green → #4CAF50
  </span>
</p>

</div>

              </div>
            )
          )}
        </div>
        <div className="p-4 border-t flex justify-end space-x-2 mt-12">
          <button
            onClick={onClose}
            className="bg-red-500 text-white px-2 py-1 rounded-md"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className={`bg-blue-500 text-white px-2 py-1 rounded-md ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""
              }`}
            disabled={isSubmitting || isLoading}
          >
            {isSubmitting ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewsEditModal;
